import { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    TextField,
    InputAdornment,
    Stack,
    MenuItem,
    Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GenericDataTable from "./GenericDataTable";
import { formatDate } from "../utils/date";

export default function ClientHistoryModal({
    open,
    onClose,
    clients = [],
    jobs = [],
    clientsMap = {},
    onViewEmail,
}) {
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedClientName, setSelectedClientName] = useState("");
    const [searchClient, setSearchClient] = useState("");

    // Filtres pour les jobs
    const [filterStatus, setFilterStatus] = useState("");
    const [filterDate, setFilterDate] = useState("");

    // Réinitialiser la recherche quand la modale s'ouvre/se ferme
    useEffect(() => {
        if (!open) {
            setSearchClient("");
            setSelectedClient(null);
            setFilterStatus("");
            setFilterDate("");
        }
    }, [open]);

    const handleSelectClient = (client) => {
        setSelectedClient(client);
        setSelectedClientName(
            client.nom || client.name || `Client #${client.id}`,
        );
    };

    const handleBack = () => {
        setSelectedClient(null);
        setSelectedClientName("");
        setFilterStatus("");
        setFilterDate("");
    };

    // Filtrer les jobs pour le client sélectionné
    const clientJobs = useMemo(() => {
        if (!selectedClient || !jobs || !Array.isArray(jobs)) return [];

        let filtered = jobs.filter(
            (job) => String(job.clientId) === String(selectedClient.id),
        );

        if (filterStatus) {
            filtered = filtered.filter((job) => {
                const jobStatus = job.status || "UNKNOWN";
                return jobStatus === filterStatus;
            });
        }

        if (filterDate) {
            filtered = filtered.filter((job) => {
                const jobDate = new Date(
                    job.statusCheckedAt ||
                        job.statusLastEmailReceivedAt ||
                        job.editedAt ||
                        job.createdAt,
                );
                const filterDateObj = new Date(filterDate);
                return (
                    jobDate.getFullYear() === filterDateObj.getFullYear() &&
                    jobDate.getMonth() === filterDateObj.getMonth() &&
                    jobDate.getDate() === filterDateObj.getDate()
                );
            });
        }

        // Tri par date la plus récente
        return filtered.sort((a, b) => {
            const dateA = new Date(
                a.statusCheckedAt ||
                    a.statusLastEmailReceivedAt ||
                    a.editedAt ||
                    a.createdAt,
            );
            const dateB = new Date(
                b.statusCheckedAt ||
                    b.statusLastEmailReceivedAt ||
                    b.editedAt ||
                    b.createdAt,
            );
            return dateB - dateA;
        });
    }, [jobs, selectedClient, filterStatus, filterDate]);

    // Préparer les données des clients pour le tableau
    const clientsData = useMemo(() => {
        if (!clients || !Array.isArray(clients)) return [];

        return clients
            .map((client) => {
                const clientJobCount =
                    jobs && Array.isArray(jobs)
                        ? jobs.filter(
                              (j) => String(j.clientId) === String(client.id),
                          ).length
                        : 0;

                return {
                    ...client,
                    displayName:
                        client.nom || client.name || `Client #${client.id}`,
                    jobCount: clientJobCount,
                };
            })
            .filter((client) => {
                if (!searchClient) return true;
                return client.displayName
                    .toLowerCase()
                    .includes(searchClient.toLowerCase());
            });
    }, [clients, jobs, searchClient]);

    // Colonnes pour la table des clients
    const clientsColumns = [
        { field: "displayName", headerName: "Nom du client", width: 300 },
        { field: "jobCount", headerName: "Nombre de sauvegardes", width: 200 },
        {
            field: "actions",
            headerName: "Actions",
            width: 150,
            renderCell: (row) => (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleSelectClient(row)}
                >
                    Voir l'historique
                </Button>
            ),
        },
    ];

    // Columns pour la table des jobs du client
    const jobsColumns = [
        { field: "name", headerName: "Sauvegarde", width: 200 },
        {
            field: "status",
            headerName: "Statut",
            renderCell: (row) => {
                const statusConfig = {
                    OK: { label: "OK", color: "success.main" },
                    WARNING: { label: "WARNING", color: "warning.main" },
                    FAILED: { label: "FAILED", color: "error.main" },
                    UNKNOWN: { label: "UNKNOWN", color: "text.disabled" },
                };
                const jobStatus = row.status || "UNKNOWN";
                const config = statusConfig[jobStatus] || statusConfig.UNKNOWN;

                const causeLines = {
                    WARNING: [
                        "⚠️ Emails d'alerte détectés (mots-clés d'alerte trouvés)",
                        "⚠️ Nombre d'emails reçus inférieur au nombre attendu",
                    ],
                    FAILED: [
                        "❌ Emails d'échec détectés (mots-clés d'échec trouvés)",
                        "❌ La sauvegarde a explicitement signalé une erreur",
                    ],
                    UNKNOWN: [
                        "❓ Aucune analyse effectuée pour ce job",
                        "❓ Aucun email reçu sur la période analysée",
                    ],
                };

                const needsTooltip = ["WARNING", "FAILED", "UNKNOWN"].includes(
                    jobStatus,
                );

                const label = (
                    <Box
                        sx={{
                            color: config.color,
                            cursor: needsTooltip ? "help" : "default",
                        }}
                    >
                        {config.label}
                    </Box>
                );

                if (!needsTooltip) return label;

                const tooltipContent = (
                    <Box sx={{ p: 0.5, maxWidth: 300 }}>
                        {row.statusSummary && (
                            <Typography
                                variant="body2"
                                sx={{ mb: 1, fontWeight: 600 }}
                            >
                                {row.statusSummary}
                            </Typography>
                        )}
                        <Typography
                            variant="caption"
                            display="block"
                            sx={{ mb: 0.5, opacity: 0.8 }}
                        >
                            Causes possibles :
                        </Typography>
                        {(causeLines[jobStatus] || []).map((line, i) => (
                            <Typography
                                key={i}
                                variant="caption"
                                display="block"
                            >
                                {line}
                            </Typography>
                        ))}
                    </Box>
                );

                return (
                    <Tooltip
                        title={tooltipContent}
                        arrow
                        placement="top"
                        componentsProps={{
                            tooltip: {
                                sx: { bgcolor: "grey.900", maxWidth: 320 },
                            },
                            arrow: { sx: { color: "grey.900" } },
                        }}
                    >
                        <span>{label}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "createdAt",
            headerName: "Dernière analyse",
            renderCell: (row) =>
                formatDate(
                    row.statusCheckedAt ||
                        row.statusLastEmailReceivedAt ||
                        row.editedAt ||
                        row.createdAt,
                ),
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                "& .MuiDialog-paper": {
                    maxHeight: "80vh",
                },
            }}
        >
            {!selectedClient ? (
                <>
                    <DialogTitle>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", sm: "center" }}
                            spacing={2}
                        >
                            <Typography variant="h6">
                                Historique des sauvegardes par client
                            </Typography>
                            <TextField
                                size="small"
                                placeholder="Rechercher un client..."
                                value={searchClient}
                                onChange={(e) =>
                                    setSearchClient(e.target.value)
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: "100%", sm: 300 } }}
                            />
                        </Stack>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ p: 2 }}>
                        <GenericDataTable
                            rows={clientsData}
                            columns={clientsColumns}
                            loading={false}
                            bordered
                            headerAlign="center"
                            cellAlign="center"
                            headerSx={{
                                fontSize: 14,
                                backgroundColor: "primary.main",
                                color: "primary.contrastText",
                            }}
                            cellSx={{ fontSize: 13 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} color="primary">
                            Fermer
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle>
                        Sauvegardes de {selectedClientName}
                    </DialogTitle>
                    <Divider />
                    <Box sx={{ p: 2, pb: 0 }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <TextField
                                select
                                size="small"
                                label="Statut"
                                value={filterStatus}
                                onChange={(e) =>
                                    setFilterStatus(e.target.value)
                                }
                                sx={{
                                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                    minWidth: { sm: 150 },
                                }}
                            >
                                <MenuItem value="">Tous</MenuItem>
                                <MenuItem value="OK">OK</MenuItem>
                                <MenuItem value="WARNING">WARNING</MenuItem>
                                <MenuItem value="FAILED">FAILED</MenuItem>
                                <MenuItem value="UNKNOWN">UNKNOWN</MenuItem>
                            </TextField>
                            <TextField
                                type="date"
                                size="small"
                                label="Date de mise à jour"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                                    minWidth: { sm: 180 },
                                }}
                            />
                        </Stack>
                    </Box>
                    <DialogContent sx={{ p: 2 }}>
                        {clientJobs.length > 0 ? (
                            <GenericDataTable
                                rows={clientJobs}
                                columns={jobsColumns}
                                onRowClick={(row) =>
                                    onViewEmail && onViewEmail(row)
                                }
                                loading={false}
                                bordered
                                headerAlign="center"
                                cellAlign="center"
                                headerSx={{
                                    fontSize: 14,
                                    backgroundColor: "primary.main",
                                    color: "primary.contrastText",
                                }}
                                cellSx={{ fontSize: 13 }}
                            />
                        ) : (
                            <Box sx={{ p: 3, textAlign: "center" }}>
                                <Typography color="textSecondary">
                                    Aucune sauvegarde pour ce client
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleBack} color="primary">
                            Retour
                        </Button>
                        <Button onClick={onClose} color="primary">
                            Fermer
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
